<template>
  <div class="space-y-6">
    <!-- Header Icon + Title -->
    <div class="text-center">
      <div
        class="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
        :class="
          hasErrors
            ? 'bg-red-100 dark:bg-red-900'
            : 'bg-green-100 dark:bg-green-900'
        "
      >
        <vue-feather
          :type="hasErrors ? 'alert-circle' : 'check-circle'"
          class="w-8 h-8"
          :class="
            hasErrors
              ? 'text-red-600 dark:text-red-400'
              : 'text-green-600 dark:text-green-400'
          "
        />
      </div>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {{
          hasErrors
            ? "Sync Completed with Errors"
            : "Sync Completed Successfully"
        }}
      </h3>
      <p class="text-gray-500 dark:text-gray-400">
        {{ safeTotal }} items processed
      </p>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-3 gap-4">
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
        <div class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ safeTotal }}
        </div>
        <div class="text-sm text-gray-500 dark:text-gray-400">Total Items</div>
      </div>
      <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
        <div class="text-2xl font-bold text-green-600 dark:text-green-400">
          {{ safeCreated }}
        </div>
        <div class="text-sm text-green-700 dark:text-green-300">Created</div>
      </div>
      <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
        <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">
          {{ safeUpdated }}
        </div>
        <div class="text-sm text-blue-700 dark:text-blue-300">Updated</div>
      </div>
    </div>

    <!-- Skipped Summary -->
    <div
      v-if="skippedCount > 0"
      class="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4"
    >
      <div class="flex items-center justify-between mb-3">
        <h4
          class="font-medium text-yellow-900 dark:text-yellow-100 flex items-center"
        >
          <vue-feather type="alert-octagon" class="w-4 h-4 mr-2" />
          Skipped Rows ({{ skippedCount }})
        </h4>
        <div class="flex items-center gap-2">
          <button
            class="px-3 py-1.5 text-xs rounded bg-yellow-600 text-white hover:bg-yellow-700"
            @click="downloadSkippedCsv"
          >
            Download CSV
          </button>
        </div>
      </div>
      <div class="space-y-2 max-h-56 overflow-y-auto">
        <div v-for="(s, sidx) in safeSkipped" :key="sidx" class="text-sm">
          <div
            class="text-yellow-800 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900/30 rounded p-2"
          >
            <span class="font-medium">Row {{ s.row }}:</span>
            {{ s.reason }}
          </div>
          <div
            v-if="Array.isArray(s.errors) && s.errors.length"
            class="mt-1 ml-4 text-xs text-yellow-700 dark:text-yellow-300"
          >
            <div v-for="(se, eidx) in s.errors" :key="eidx">- {{ se }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Errors List -->
    <div v-if="hasErrors" class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
      <div class="flex items-center justify-between mb-3">
        <h4
          class="font-medium text-red-900 dark:text-red-100 flex items-center"
        >
          <vue-feather type="alert-triangle" class="w-4 h-4 mr-2" />
          Errors Found ({{ errorCount }})
        </h4>
        <div class="flex items-center gap-2">
          <button
            class="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700"
            @click="downloadErrorsCsv"
          >
            Download CSV
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
            @click="downloadCorrectionsCsv"
          >
            Download for Correction
          </button>
        </div>
      </div>
      <div class="space-y-2 max-h-56 overflow-y-auto">
        <div
          v-for="(err, idx) in safeErrors"
          :key="idx"
          class="text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded p-2"
        >
          <span class="font-medium">Row {{ err.row }}:</span>
          {{ err.error }}
          <div v-if="err.column" class="text-xs opacity-80">
            Column: {{ err.column }}
          </div>
          <div v-if="err.rowValues" class="text-xs opacity-80 truncate">
            Row values: {{ err.rowValues }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SyncTaskResults",
  props: {
    result: {
      type: Object,
      required: true,
    },
  },
  computed: {
    safeTotal() {
      return Number(this.result?.total ?? this.result?.items_total ?? 0);
    },
    safeCreated() {
      return Number(this.result?.created ?? this.result?.items_created ?? 0);
    },
    safeUpdated() {
      return Number(this.result?.updated ?? this.result?.items_updated ?? 0);
    },
    safeErrors() {
      const raw = this.result?.errors ?? this.result?.failures ?? [];
      const arr = Array.isArray(raw) ? raw : [];
      return arr.map((e) => ({
        row: e?.row ?? "-",
        error: e?.error ?? String(e ?? ""),
        rowValues: e?.row_values ?? e?.rowValues ?? null,
        column: e?.column ?? null,
      }));
    },
    safeSkipped() {
      const arr = Array.isArray(this.result?.skipped_rows)
        ? this.result.skipped_rows
        : Array.isArray(this.result?.skipped)
        ? this.result.skipped
        : [];
      return arr.map((e) => ({
        row: e?.row ?? "-",
        reason: e?.reason ?? String(e ?? ""),
        rowValues: e?.row_values ?? e?.rowValues ?? null,
      }));
    },
    hasErrors() {
      return this.safeErrors.length > 0;
    },
    errorCount() {

      return this.safeErrors.length;
    },
    skippedCount() {
      return this.safeSkipped.length;
    },
  },
  methods: {
    // naive CSV parser for a single line - handles simple quoted fields
    parseCsvLine(line) {
      if (!line && line !== "") return [];
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          // Handle escaped quotes
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += ch;
        }
      }
      result.push(current);
      return result.map((v) => (v == null ? "" : String(v)));
    },
    downloadErrorsCsv() {
      const header = ["row", "error"];
      const rows = this.safeErrors.map((e) => [
        e.row,
        `"${(e.error || "").replace(/"/g, '""')}"`,
      ]);
      const csv = [header.join(","), ...rows.map((r) => r.join(","))].join(
        "\n"
      );
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sync_errors.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    downloadCorrectionsCsv() {
      // Log the result for debugging (no fallback headers)
      // eslint-disable-next-line no-console
      console.log('[SyncTaskResults] corrections export result:', this.result);

      // Use human-readable headers only if provided by backend (no fallback)
      const humanHeaders = Array.isArray(this.result?.headers)
        ? this.result.headers
        : [];

      // eslint-disable-next-line no-console
      console.log('[SyncTaskResults] humanHeaders:', humanHeaders);
        
      // Group rows by row number and aggregate errors
      const rowMap = new Map();
      const addRow = (rowNum, rowValues, errorText) => {
        const num = Number(rowNum);
        const key = Number.isFinite(num) ? num : rowNum;
        if (!rowMap.has(key)) {
          const values = Array.isArray(rowValues)
            ? rowValues
            : typeof rowValues === "string"
            ? this.parseCsvLine(rowValues)
            : [];
          rowMap.set(key, { values, errors: [] });
        }
        if (errorText) {
          rowMap.get(key).errors.push(String(errorText));
        }
      };

      this.safeErrors.forEach((e) => {
        const prefix = e.column ? `${e.column}: ` : "";
        addRow(e.row, e.rowValues, `${prefix}${e.error}`);
      });
      this.safeSkipped.forEach((s) => {
        addRow(s.row, s.rowValues, `Skipped: ${s.reason}`);
      });

      // Determine max number of error columns
      let maxErrors = 0;
      rowMap.forEach((v) => {
        maxErrors = Math.max(maxErrors, v.errors.length || 0);
      });

      const errorCols = Array.from({ length: maxErrors }, (_, i) => `Error ${i + 1}`);
      const headerRow = [...humanHeaders, ...errorCols];

      // Build CSV rows in ascending row order
      const sortedKeys = Array.from(rowMap.keys()).sort((a, b) => {
        const an = typeof a === "number";
        const bn = typeof b === "number";
        if (an && bn) return a - b;
        if (an) return -1;
        if (bn) return 1;
        return String(a).localeCompare(String(b));
      });
      const csvRows = [headerRow.join(",")];
      sortedKeys.forEach((k) => {
        const { values, errors } = rowMap.get(k);
        // Ensure values length matches headers length for alignment
        const padded = [...values];
        if (humanHeaders.length && padded.length < humanHeaders.length) {
          for (let i = padded.length; i < humanHeaders.length; i++) padded.push("");
        }
        const vals = (
          humanHeaders.length ? padded.slice(0, humanHeaders.length) : padded
        ).map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`);
        const errCells = errorCols.map((_, i) =>
          errors[i] ? `"${errors[i].replace(/"/g, '""')}"` : ""
        );
        csvRows.push([...vals, ...errCells].join(","));
      });

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sync_corrections.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    downloadSkippedCsv() {
      const header = ["row", "reason"];
      const rows = this.safeSkipped.map((e) => [
        e.row,
        `"${(e.reason || "").replace(/"/g, '""')}"`,
      ]);
      const csv = [header.join(","), ...rows.map((r) => r.join(","))].join(
        "\n"
      );
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sync_skipped_rows.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped></style>
